import { dictionaryApi } from './dictionaryClient';
import type { Language } from './languageApi';

export type TingantaArtha = {
  id: number;
  tingantaId: number;
  languageId: number;
  text: string;
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  language?: Language | null;
};

export type CreateTingantaArthaBody = {
  tingantaId: number;
  languageId: number;
  text: string;
};

export type PatchTingantaArthaBody = Partial<Pick<TingantaArtha, 'text' | 'languageId'>>;

function normalizeList(data: unknown): TingantaArtha[] {
  if (Array.isArray(data)) return data as TingantaArtha[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: TingantaArtha[] }).data;
  }
  return [];
}

export function isTingantaArtha(x: unknown): x is TingantaArtha {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    typeof o.tingantaId === 'number' &&
    typeof o.languageId === 'number' &&
    typeof o.text === 'string'
  );
}

export function parseTingantaArthas(raw: unknown[] | undefined): TingantaArtha[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isTingantaArtha);
}

export async function listTingantaArthas(tingantaId?: number): Promise<TingantaArtha[]> {
  const res = await dictionaryApi.get<unknown>('/tinganta-artha', {
    params: tingantaId != null ? { tingantaId } : undefined,
  });
  return normalizeList(res.data);
}

export async function getTingantaArtha(id: number): Promise<TingantaArtha> {
  const res = await dictionaryApi.get<TingantaArtha>(`/tinganta-artha/${id}`);
  return res.data;
}

export async function createTingantaArtha(body: CreateTingantaArthaBody): Promise<TingantaArtha> {
  const res = await dictionaryApi.post<TingantaArtha>('/tinganta-artha', body);
  return res.data;
}

export async function updateTingantaArtha(
  id: number,
  body: PatchTingantaArthaBody
): Promise<TingantaArtha> {
  const res = await dictionaryApi.patch<TingantaArtha>(`/tinganta-artha/${id}`, body);
  return res.data;
}

export async function deleteTingantaArtha(id: number): Promise<void> {
  await dictionaryApi.delete(`/tinganta-artha/${id}`);
}

export function buildTingantaArthaPatch(
  prev: TingantaArtha,
  next: Pick<TingantaArtha, 'text' | 'languageId'>
): PatchTingantaArthaBody {
  const patch: PatchTingantaArthaBody = {};
  if (prev.languageId !== next.languageId) patch.languageId = next.languageId;
  if (prev.text !== next.text) patch.text = next.text.trim();
  return patch;
}

/** Output of {@link parseTingantaArthaStructuredText} (e.g. `+प्र- @१. … @२. …`). */
export type ParsedTingantaArthaText = {
  upasarga: string;
  meanings: string[];
};

const DEVANAGARI_DIGIT = '\u0966-\u096F';

/**
 * Parses tiṅganta-artha free text like:
 * `+प्र- @१. जानु, @२. दृष्टिगोचर हुनु, देखिनु, @३. प्रकट हुनु, @४. शासन गर्नु ।`
 * — optional `+upasarga-` prefix, then segments marked by `@` + Devanagari/Arabic digit + `.`.
 */
export function parseTingantaArthaStructuredText(raw: string): ParsedTingantaArthaText {
  const s = raw == null ? '' : String(raw).trim();
  if (!s) return { upasarga: '', meanings: [] };

  let rest = s;
  let upasarga = '';
  const upa = rest.match(/^\s*\+(.+?)-\s*/);
  if (upa) {
    upasarga = upa[1].trim();
    rest = rest.slice(upa[0].length).trim();
  }

  const trimMeaning = (p: string) =>
    p
      .trim()
      .replace(/,\s*$/u, '')
      .trim()
      .replace(/\s*।+$/u, '')
      .trim();

  if (!rest.includes('@')) {
    const single = trimMeaning(rest);
    return { upasarga, meanings: single ? [single] : [] };
  }

  const reSplit = new RegExp(`@\\s*[${DEVANAGARI_DIGIT}0-9]+\\.\\s*`);
  const parts = rest.split(reSplit);
  const meanings = parts.map(trimMeaning).filter((p) => p.length > 0);

  return { upasarga, meanings };
}
